import { cn } from "@/lib/utils";
import { acceptErrorMessage, doesFileMatchAccept } from "@/utils/file-accept";
import {
  createContext,
  useContext,
  useId,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { FileFormatIcon, type FileFormat } from "./file-format-icon";

const iconFormat = (filename: string): FileFormat => {
  const name = filename.toLowerCase();
  if (name.endsWith(".dsd")) return "dsd";
  if (name.endsWith(".ixd")) return "xbrl";
  return "excel";
};

export const formatSize = (bytes: number) =>
  bytes < 1024 * 1024
    ? `${(bytes / 1024).toFixed(1)}KB`
    : `${(bytes / (1024 * 1024)).toFixed(1)}MB`;

const DropAreaContext = createContext<
  { inputId: string; clearError: () => void } | undefined
>(undefined);

/**
 * 영역 안 숨은 file input 의 id. children(고른 파일 칸 등)에서 `<label htmlFor>` 로 파일 창을 열 때 쓴다 —
 * children 은 label 밖이라 암시적 label 활성화가 닿지 않는다.
 */
export const useFileInputId = () => useContext(DropAreaContext)?.inputId;

/** 업로드 영역 아래 인라인 오류 — 한 번에 한 줄만 둔다. */
const UploadError = ({ message }: { message: string | null }) =>
  message ? (
    <p role="alert" className="label-2-regular w-full text-status-error">
      {message}
    </p>
  ) : null;

/**
 * Figma `file_uploadZone` 의 틀 — 클릭하거나 파일을 끌어 놓아 고른다. 고른 파일 표시는 children 이 맡는다.
 * 한 번에 `maxFiles` 개까지 받아 고른 순서대로 한 번에 onFilesSelect 로 넘긴다.
 *
 * WHY: label 로 sr-only input 을 여는 건 브라우저 기본 동작에 맡긴다. 단 label 은 위쪽 안내 영역만 감싼다 —
 * 영역 전체가 클릭 영역이면 고른 파일 카드(제거 버튼 포함)를 누를 때 파일 창이 함께 열려 충돌한다.
 * 드래그&드롭은 children 을 포함한 영역 전체에서 받는다. children 안에서도 클릭으로 열어야 하는 자리(빈 칸)는
 * `useFileInputId()` + `<label htmlFor>` 로 명시해 연다.
 */
export const FileDropArea = ({
  accept,
  hint,
  header,
  maxFiles = 1,
  selectedHint,
  compactWhenSelected = false,
  className,
  error,
  onFilesSelect,
  onReject,
  children,
}: {
  accept: string;
  hint: string;
  /** 영역 맨 위 줄(제목·탭 등). label 밖이라 눌러도 파일 창이 열리지 않는다. */
  header?: ReactNode;
  /** 한 번에 받을 파일 수 상한. 넘겨 고르면 하나도 받지 않고 알린다. */
  maxFiles?: number;
  /** 이미 고른 파일이 있을 때 보여줄 안내. 없으면 기본 안내를 그대로 둔다. */
  selectedHint?: string;
  /**
   * `selectedHint` 가 있을 때만(= 파일을 고른 상태) 위쪽 안내를 Figma `file_uploadZone`
   * 선택 상태(아이콘 24px, 위아래 12px)로 줄인다. 고르기 전에는 기본 크기 그대로다.
   * 높이가 낮은 영역(버전 업데이트 모달 210px)은 기본 크기로는 파일 카드와 함께 들어가지 않는다.
   */
  compactWhenSelected?: boolean;
  /** 영역 높이 등 바깥 크기. 기본 285px — tailwind-merge 가 `h-[285px]` 를 덮으므로 높이도 여기서 바꾼다. */
  className?: string;
  /**
   * 호출부가 직접 판정한 오류 문구(중복·칸 초과 등). 영역 아래 오류 줄은 한 줄뿐이므로
   * 기본 오류(형식·상한)보다 이 문구가 먼저다.
   */
  error?: string | null;
  /**
   * 드롭을 통째로 막았을 때(상한 초과 `count`, 전부 형식 불일치 `format`). 주면 영역이
   * 오류 줄을 직접 띄우지 않고 호출부가 `error` 로 문구를 정한다 — 중복 같은 자기 판정과
   * 같은 줄을 두고 다투지 않게.
   * 일부만 형식에 걸린 드롭은 받을 것을 받으므로 여기 대신 `onFilesSelect` 의 `rejected` 로 간다.
   */
  onReject?: (reason: { kind: "count" | "format"; message: string }) => void;
  /**
   * 한 번에 고른 파일을 통째로 넘긴다 — 칸 배정은 호출부가 한다.
   * 파일 하나씩 부르면 호출부가 "한 드롭 안에서 앞 파일을 덮었는지"를 알 수 없다.
   * `rejected` 는 형식이 맞지 않아 버린 개수 — 어느 문구를 보여줄지 호출부가 한 곳에서 정한다.
   */
  onFilesSelect: (files: File[], info: { rejected: number }) => void;
  children?: ReactNode;
}) => {
  /**
   * 형식·상한 오류는 영역이 직접 아래에 보여준다. `onReject` 를 준 호출부는 자기 판정을
   * `error` 로 넘기므로, 한 줄을 두고 다투지 않도록 기본 문구를 켜지 않는다.
   */
  const [selfError, setSelfError] = useState<string | null>(null);

  const reject = (kind: "count" | "format", message: string) => {
    if (onReject) onReject({ kind, message });
    else setSelfError(message);
  };

  const selectFiles = (files: FileList | null | undefined) => {
    const picked = Array.from(files ?? []);
    // 상한을 넘기면 하나도 받지 않는다 — 앞에서 몇 개만 가져오면 고른 것과 다른 결과가 된다
    if (picked.length > maxFiles) {
      reject("count", `한 번에 ${maxFiles}개까지 선택할 수 있습니다.`);
      return;
    }
    const accepted = picked.filter((file) => doesFileMatchAccept(file, accept));
    const rejected = picked.length - accepted.length;
    if (accepted.length === 0) {
      if (rejected > 0) reject("format", acceptErrorMessage(accept));
      return;
    }
    // 일부만 형식에 걸린 드롭은 받을 것을 받고 `rejected` 로 알린다 — 두 경로가
    // 같은 오류 줄을 두고 다투지 않도록 `onReject` 는 부르지 않는다.
    if (!onReject)
      setSelfError(rejected > 0 ? acceptErrorMessage(accept) : null);
    onFilesSelect(accepted, { rejected });
  };
  const [isDragging, setIsDragging] = useState(false);
  // 자식 요소를 지날 때마다 dragenter/leave 가 번갈아 나므로 깊이로 영역 안팎을 판정한다
  const dragDepth = useRef(0);
  const isCompact = compactWhenSelected && selectedHint !== undefined;
  const inputId = useId();

  return (
    // 오류 줄은 영역을 감싸지 않고 형제로 둔다 — 감싸면 호출부가 className 으로 준 높이·flex 가 깨진다
    <>
      {/* hover·드래그 색은 기존 변환 화면(team-workspace/dsd-xlsx-convert)과 같다 */}
      <div
        className={cn(
          "flex h-[285px] w-full flex-col gap-2.5 rounded-lg border-2 border-dashed p-4 transition-all duration-200 ease-in-out focus-within:border-blue-400",
          isDragging
            ? "scale-[1.01] border-blue-400 bg-blue-50/60"
            : // hover 하이라이트는 클릭 가능한 자리(label)에 올렸을 때만 — 파일 카드·패딩에 뜨면 눌러도 반응이 없다
              "border-line-normal has-[label:hover]:border-blue-300 has-[label:hover]:bg-blue-50/30",
          className,
        )}
        onDragEnter={(e) => {
          e.preventDefault();
          dragDepth.current += 1;
          setIsDragging(true);
        }}
        onDragLeave={(e) => {
          e.preventDefault();
          dragDepth.current -= 1;
          if (dragDepth.current === 0) setIsDragging(false);
        }}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          dragDepth.current = 0;
          setIsDragging(false);
          selectFiles(e.dataTransfer.files);
        }}
      >
        {header}
        {/* 클릭으로 파일 창을 여는 영역은 여기까지 — 아래 children(파일 카드)은 label 밖이다 */}
        <label
          className={cn(
            "flex min-h-0 flex-1 cursor-pointer flex-col items-center justify-center gap-2",
            isCompact ? "py-3" : "py-10",
          )}
        >
          <input
            id={inputId}
            type="file"
            accept={accept}
            multiple={maxFiles > 1}
            className="sr-only"
            onChange={(e) => {
              selectFiles(e.target.files);
              // 같은 파일을 지웠다가 다시 골라도 change 가 나도록 비운다
              e.target.value = "";
            }}
          />
          {/* 26.67×33.33 글리프가 40px 칸 가운데 — Figma inset 8.33%/16.67% 와 같은 자리 */}
          <div
            className={cn(
              "flex shrink-0 items-center justify-center opacity-30",
              isCompact ? "size-6" : "size-10",
            )}
          >
            <img
              alt=""
              src="/icons/file-upload.svg"
              className={isCompact ? "h-5 w-4" : undefined}
            />
          </div>
          {isDragging ? (
            <p className="label-2-regular text-center text-blue-600">
              여기에 파일을 놓아주세요
            </p>
          ) : selectedHint ? (
            <p className="caption-1-regular text-center text-label-alternative">
              {selectedHint}
            </p>
          ) : (
            <div className="flex flex-col items-center gap-1 text-center">
              <p className="label-2-regular text-label-neutral">
                클릭하거나 파일을 드래그해서 업로드
              </p>
              <p className="caption-1-regular text-label-alternative">{hint}</p>
            </div>
          )}
        </label>
        <DropAreaContext.Provider
          value={{ inputId, clearError: () => setSelfError(null) }}
        >
          {children}
        </DropAreaContext.Provider>
      </div>
      <UploadError message={error ?? selfError} />
    </>
  );
};

/**
 * 업로드 영역 안에 고른 파일을 보여주는 카드 틀 — 회색 카드, 40px 흰 아이콘 칸, 제거 버튼.
 * 가운데 칸 내용은 children 이 채운다(파일명+크기, 뱃지+파일명 등 자리 순서가 호출부마다 다르다).
 */
export const SelectedFileCard = ({
  format,
  onRemove,
  children,
}: {
  format: FileFormat;
  onRemove: () => void;
  children: ReactNode;
}) => {
  // 파일을 지우면 그 파일을 고를 때 뜬 오류 줄도 함께 거둔다
  const clearError = useContext(DropAreaContext)?.clearError;

  return (
    <div className="flex items-center gap-3 rounded-lg bg-background-gray-alternative p-4">
      <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-white">
        <FileFormatIcon format={format} className="size-9" />
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-1">{children}</div>
      <button
        type="button"
        aria-label="파일 제거"
        // 카드 배경이 gray-100 이라 기존 화면의 neutral-100 hover 는 안 보여 한 단계 진하게
        className="-m-1.5 shrink-0 rounded-lg p-1.5 transition-colors duration-150 hover:bg-neutral-200"
        onClick={() => {
          clearError?.();
          onRemove();
        }}
      >
        <img alt="" src="/icons/cancel-rounded.svg" className="size-5" />
      </button>
    </div>
  );
};

/** 한 개만 고르는 업로드 영역. 고른 파일 카드와 제거 버튼을 같은 영역 안에 보여준다. */
export const FileDropZone = ({
  accept,
  hint,
  header,
  className,
  file,
  onFileChange,
}: {
  accept: string;
  hint: string;
  header?: ReactNode;
  className?: string;
  file: File | null;
  onFileChange: (file: File | null) => void;
}) => (
  <FileDropArea
    accept={accept}
    hint={hint}
    header={header}
    className={className}
    selectedHint={
      file ? "다른 파일로 교체하려면 클릭하거나 드래그하세요" : undefined
    }
    onFilesSelect={(files) => onFileChange(files[0] ?? null)}
  >
    {file && (
      <div className="flex flex-col gap-2">
        <div className="h-px w-full bg-line-normal" />
        <SelectedFileCard
          format={iconFormat(file.name)}
          onRemove={() => onFileChange(null)}
        >
          <p className="label-1-normal-regular truncate text-label-normal">
            {file.name}
          </p>
          <p className="caption-1-regular truncate text-label-assistive">
            {formatSize(file.size)}
          </p>
        </SelectedFileCard>
      </div>
    )}
  </FileDropArea>
);
